// src/components/SettingsModal.js

import React, { useContext, useState, useEffect } from 'react';
import { Modal, Button, Form, Tabs, Tab, Card, Row, Col } from 'react-bootstrap';
import { SettingsContext } from '../contexts/SettingsContext';
import { LanguageContext } from '../contexts/LanguageContext';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useAlertDialog from '../hooks/useAlertDialog';
import './SettingsModal.css'

// New React component for the custom SVG icon
const LayoutSidebarInsetIcon = ({ size, style }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    fill="currentColor"
    className="bi bi-layout-sidebar-inset" // Changed to className
    viewBox="0 0 16 16"
    style={style}
  >
    <path d="M14 2a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1zM2 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2z"/>
    <path d="M3 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/>
  </svg>
);

// New React component for the combined custom SVG icon
const LayoutSidebarInsetBothIcon = ({ size }) => (
  <div style={{ position: 'relative', width: size, height: size }}>
    <LayoutSidebarInsetIcon
      size={size}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: 'rotate(90deg)', // Filled down
      }}
    />
    <LayoutSidebarInsetIcon
      size={size}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        transform: 'rotate(270deg)', // Filled up
      }}
    />
  </div>
);

// -------------------------
// Sub-components for Settings Tabs
// -------------------------

const ResultsTab = ({
  mode,
  setMode,
  defaultResults,
  setDefaultResults,
  furnitureResults,
  setFurnitureResults,
  lightResults,
  setLightResults,
  accessoriesResults,
  setAccessoriesResults,
  translations,
  options,
}) => (
  <Form>
    <Form.Group controlId="modeSelect" className="mb-3">
      <Form.Label>{translations.resultsMode}</Form.Label>
      <Form.Check
        type="radio"
        id="modeSelectDefault"
        label={translations.defaultMode}
        name="resultsMode"
        checked={mode === 'default'}
        onChange={() => setMode('default')}
      />
      <Form.Check
        type="radio"
        id="modeSelectCustom"
        label={translations.customMode}
        name="resultsMode"
        checked={mode === 'custom'}
        onChange={() => setMode('custom')}
      />
    </Form.Group>

    {mode === 'default' && (
      <Form.Group controlId="defaultResults" className="mb-3">
        <Form.Label>{translations.defaultResultsForAll}</Form.Label>
        <Form.Select
          value={defaultResults.toString()}
          onChange={(e) => setDefaultResults(parseInt(e.target.value, 10))}
        >
          {options.map((opt) => (
            <option key={opt} value={opt.toString()}>
              {opt}
            </option>
          ))}
        </Form.Select>
      </Form.Group>
    )}

    {mode === 'custom' && (
      <>
        <Form.Group controlId="lightResults" className="mb-3">
          <Form.Label>{translations.lightResults}</Form.Label>
          <Form.Select
            value={lightResults.toString()}
            onChange={(e) => setLightResults(parseInt(e.target.value, 10))}
          >
            {options.map((opt) => (
              <option key={opt} value={opt.toString()}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="furnitureResults" className="mb-3">
          <Form.Label>{translations.furnitureResults}</Form.Label>
          <Form.Select
            value={furnitureResults.toString()}
            onChange={(e) => setFurnitureResults(parseInt(e.target.value, 10))}
          >
            {options.map((opt) => (
              <option key={opt} value={opt.toString()}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="accessoriesResults" className="mb-3">
          <Form.Label>{translations.accessoriesResults}</Form.Label>
          <Form.Select
            value={accessoriesResults.toString()}
            onChange={(e) => setAccessoriesResults(parseInt(e.target.value, 10))}
          >
            {options.map((opt) => (
              <option key={opt} value={opt.toString()}>
                {opt}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </>
    )}
  </Form>
);

const PreferencesTab = ({
  selectedLanguage,
  setSelectedLanguage,
  theme,
  setTheme,
  fontSize,
  setFontSize,
  layout,
  setLayout,
  translations,
  fontSizeOptions,
  layoutOptions,
  handleClearCache,
  showConfirm,
}) => (
  <Form>
    {/* Language Selection */}
    <Form.Group controlId="languageSelect" className="mb-3">
      <Form.Label>{translations.languageSelection}</Form.Label>
      <Form.Select
        value={selectedLanguage}
        onChange={(e) => setSelectedLanguage(e.target.value)}
      >
        <option value="ka">ქართული</option>
        <option value="en">English</option>
      </Form.Select>
    </Form.Group>

    <Form.Group controlId="theme" className="mb-3">
      <Form.Label>{translations.themeSelection}</Form.Label>
      <Form.Select value={theme} onChange={(e) => setTheme(e.target.value)}>
        <option value="auto">{translations.autoMode}</option>
        <option value="light">{translations.lightMode}</option>
        <option value="dark">{translations.darkMode}</option>
      </Form.Select>
    </Form.Group>

    <Form.Group controlId="fontSize" className="mb-3">
      <Form.Label>{translations.fontSize}</Form.Label>
      <Form.Select value={fontSize} onChange={(e) => setFontSize(e.target.value)}>
        {fontSizeOptions.map((size) => (
          <option key={size} value={size}>
            {translations[size]}
          </option>
        ))}
      </Form.Select>
    </Form.Group>

    {/* Layout Selection */}
    <Form.Group controlId="layout" className="mb-3">
      <Form.Label>{translations.layoutSelection}</Form.Label>
      <Row className="mt-2">
        {layoutOptions.map((option) => (
          <Col key={option.value} xs={4} md={4}>
            <Card
              onClick={() => setLayout(option.value)}
              className={`h-100 text-center layout-option-card ${layout === option.value ? 'selected border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
            >
              <Card.Body className="d-flex flex-column align-items-center justify-content-center">
                {option.icon}
                <Card.Text className="mt-2">{option.label}</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </Form.Group>

    {/* Data Management */}
    <Form.Group controlId="dataManagement" className="mb-3">
      <Form.Label>{translations.dataManagement}</Form.Label>
      <div className="d-grid gap-2">
        <Button
          variant="outline-secondary"
          onClick={handleClearCache}
        >
          {translations.clearCache}
        </Button>
      </div>
    </Form.Group>

    {/* Reset Settings Option */}
    <Form.Group controlId="resetSettings" className="mb-3">
      <Button
        variant="danger"
        onClick={async () => {
          const confirmed = await showConfirm({
            title: translations.resetSettings,
            message: translations.resetSettingsConfirmation || 'Are you sure you want to reset all settings? This action cannot be undone.',
            confirmText: translations.resetSettings || 'Reset Settings',
            cancelText: translations.cancel || 'Cancel',
            variant: 'danger',
            onConfirm: () => {
              localStorage.removeItem('appSettings');
              window.location.reload();
            }
          });
        }}
      >
        {translations.resetSettings}
      </Button>
    </Form.Group>
  </Form>
);

// -------------------------
// Main SettingsModal Component
// -------------------------

function SettingsModal({ show, onClose }) {
  const { settings, updateSettings } = useContext(SettingsContext);
  const { translations, language, toggleLanguage } = useContext(LanguageContext);
  
  // Dialog hooks
  const { dialogState: confirmState, showConfirm, hideConfirm } = useConfirmDialog();
  const { dialogState: alertState, showAlert, hideAlert } = useAlertDialog();

  // Local state for Results Settings (existing)
  const [mode, setMode] = useState(settings.mode);
  const [defaultResults, setDefaultResults] = useState(settings.defaultResults);
  const [furnitureResults, setFurnitureResults] = useState(settings.furnitureResults);
  const [lightResults, setLightResults] = useState(settings.lightResults);
  const [accessoriesResults, setAccessoriesResults] = useState(settings.accessoriesResults);

  // Local state for Appearance Settings
  const [theme, setTheme] = useState(settings.theme);
  const [fontSize, setFontSize] = useState(settings.fontSize);
  const [layout, setLayout] = useState(settings.layout);

  // Local state for language selection
  const [selectedLanguage, setSelectedLanguage] = useState(language);

  const options = [10, 20, 30, 40, 50];
  const fontSizeOptions = ['small', 'medium', 'large'];
  const layoutOptions = [
    {
      value: 'bottom_only',
      label: translations.bottomNavOnly,
      icon: (
        <LayoutSidebarInsetIcon
          size={40}
          style={{
            transform: 'rotate(270deg)', // Rotate the icon by 270 degrees
            transition: 'transform 0.3s ease', // Smooth transition
          }}
        />
      ),
    },
    {
      value: 'center_only',
      label: translations.centerButtonsOnly,
      icon: (
        <LayoutSidebarInsetIcon
          size={40}
          style={{
            transform: 'rotate(90deg)', // Rotate the icon by 90 degrees
            transition: 'transform 0.3s ease', // Smooth transition
          }}
        />
      ),
    },
    {
      value: 'both',
      label: translations.bothActive,
      icon: <LayoutSidebarInsetBothIcon size={40} />, // combined icon
    },
  ];

  useEffect(() => {
    if (show) {
      // Reset local state when modal is opened
      setMode(settings.mode);
      setDefaultResults(settings.defaultResults);
      setFurnitureResults(settings.furnitureResults);
      setLightResults(settings.lightResults);
      setAccessoriesResults(settings.accessoriesResults);
      setTheme(settings.theme);
      setFontSize(settings.fontSize);
      setLayout(settings.layout);
      setSelectedLanguage(language);
    }
  }, [show, settings, language]);

  const handleClearCache = async () => {
    const confirmed = await showConfirm({
      title: translations.clearCache,
      message: translations.clearCacheConfirmation || 'Are you sure you want to clear the cache?',
      confirmText: translations.clearCache || 'Clear Cache',
      cancelText: translations.cancel || 'Cancel',
      variant: 'warning',
      onConfirm: () => {
        // Clear localStorage except for settings
        const settingsBackup = localStorage.getItem('appSettings');
        const languageBackup = localStorage.getItem('language');
        localStorage.clear();
        if (settingsBackup) localStorage.setItem('appSettings', settingsBackup);
        if (languageBackup) localStorage.setItem('language', languageBackup);
        
        // Clear sessionStorage
        sessionStorage.clear();
        
        // Clear any cached API data or other app-specific caches
        if ('caches' in window) {
          caches.keys().then(names => {
            names.forEach(name => {
              caches.delete(name);
            });
          });
        }
        
        // Show success alert
        showAlert({
          title: translations.success || 'Success',
          message: translations.cacheCleared || 'Cache cleared successfully!',
          variant: 'success',
          autoClose: true,
          autoCloseDelay: 2000,
        });
      }
    });
  };

  const handleSave = (e) => {
    e.preventDefault();
    // Update Settings with new options
    updateSettings({
      mode,
      defaultResults,
      furnitureResults,
      lightResults,
      accessoriesResults,
      // Update Appearance Settings
      theme,
      fontSize,
      layout,
    });

    // Handle language change
    toggleLanguage(selectedLanguage);

    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <>
      <Modal show={show} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{translations.settings}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Tabs defaultActiveKey="results" id="settings-tabs" className="mb-3">
            {/* Results Tab */}
            <Tab eventKey="results" title={translations.results}>
              <ResultsTab
                mode={mode}
                setMode={setMode}
                defaultResults={defaultResults}
                setDefaultResults={setDefaultResults}
                furnitureResults={furnitureResults}
                setFurnitureResults={setFurnitureResults}
                lightResults={lightResults}
                setLightResults={setLightResults}
                accessoriesResults={accessoriesResults}
                setAccessoriesResults={setAccessoriesResults}
                translations={translations}
                options={options}
              />
            </Tab>

            {/* Preferences Tab */}
            <Tab eventKey="preferences" title={translations.general || translations.appearance || 'Preferences'}>
              <PreferencesTab
                selectedLanguage={selectedLanguage}
                setSelectedLanguage={setSelectedLanguage}
                theme={theme}
                setTheme={setTheme}
                fontSize={fontSize}
                setFontSize={setFontSize}
                layout={layout}
                setLayout={setLayout}
                translations={translations}
                fontSizeOptions={fontSizeOptions}
                layoutOptions={layoutOptions}
                handleClearCache={handleClearCache}
                showConfirm={showConfirm}
              />
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancel}>
            {translations.cancel}
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {translations.save}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Custom Dialogs */}
      <ConfirmDialog
        show={confirmState.show}
        onHide={hideConfirm}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
        icon={confirmState.icon}
      />

      <AlertDialog
        show={alertState.show}
        onHide={hideAlert}
        title={alertState.title}
        message={alertState.message}
        variant={alertState.variant}
        icon={alertState.icon}
        autoClose={alertState.autoClose}
        autoCloseDelay={alertState.autoCloseDelay}
        showCloseButton={alertState.showCloseButton}
      />
    </>
  );
}

export default SettingsModal;
